import {useCallback, useEffect, useState} from "react";
import { useLocation, useParams } from "react-router-dom";
import BackButton from "../../../../components/fundamentals/back-button";
import DownloadIcon from "../../../../components/fundamentals/icons/download-icon";
import Section from "../../../../components/fundamentals/section";
import Spinner from "../../../../components/fundamentals/spinner";
import StatusSelector from "../../../../components/fundamentals/status-selector";
import NotFound from "../../../../components/NotFound/NotFound";
import { Product } from "../../../../models/Product";
import productsService from "../../../../services/products.service";
import useNotification from "../../../../hooks/use-notification";
import useImperativeDialog from "../../../../hooks/use-imperative-dialog";
import Button from "../../../../components/fundamentals/button";
import RefreshIcon from "../../../../components/fundamentals/icons/refresh-icon";
import syncService from "../../../../services/sync.service";
import { sourceLabel } from "../../../../models/Supplier";
import DateHelper from "../../../../models/utils/DateHelper";
import {ProductDocument} from "../../../../models/ProductDocument";
import {Stock} from "../../../../models/Stock";
import agencesService from "../../../../services/agences.service";
import {Agency} from "../../../../models/Agency";
import CategoryPicker from "../../category-mappings/CategoryPicker";
import { Category } from "../../../../models/Category";

export default function ProductDetailPage() {
  let { id } = useParams();
  const [product, setProduct] = useState<Product>();
  const [productDocuments, setProductDocuments] = useState<ProductDocument[]>([]);
  const [productStocks, setProductStocks] = useState<Stock[]>([]);
  const [productAgencies, setProductAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [isResyncing, setIsResyncing] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const location = useLocation();
  const notificationToast = useNotification();
  const dialog = useImperativeDialog();

  useEffect(() => {
    async function fetchProduct() {
      try {
        setLoading(true);
        if (id) {
          setProduct(await productsService.find(Number(id)));

          setProductDocuments(await productsService.findProductDocuments(Number(id)));

          const stocks = await productsService.findProductStock(Number(id))

          setProductStocks(stocks);

          const agencies = await Promise.all(
              stocks.map(async ({stock, agencyId}) => {
                return await agencesService.find(agencyId);
              })
          );

          console.log(agencies);

          setProductAgencies(agencies);
        }
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    }
    fetchProduct();
  }, [id]);

  const changeVisibility = useCallback(async () => {
    if (!product) return;

    try {
      setLoading(true);

      const updatedProductId = await productsService.changeVisibility(product.id, product.is_disabled);

      setProduct(await productsService.find(updatedProductId));
    } catch (e: any) {
      notificationToast("Erreur", e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [product]);

  // RAG-38 : resynchroniser ce produit depuis son PIM de référence.
  const handleResync = useCallback(async () => {
    if (!product) return;

    const confirmed = await dialog({
      heading: "Resynchroniser le produit",
      text: `Recharger les données de « ${product.name} » depuis son PIM de référence ?`,
      confirmText: "Oui, resynchroniser",
    });
    if (!confirmed) return;

    try {
      setIsResyncing(true);
      const result = await syncService.run("product", product.id);
      if (result.httpStatus === 204) {
        notificationToast(
          "Produit introuvable dans le PIM",
          "Cet EAN n'existe pas dans son PIM de référence : rien à mettre à jour.",
          "warning"
        );
      } else {
        notificationToast("Succès", "Produit resynchronisé depuis le PIM.", "success");
      }
      setProduct(await productsService.find(product.id));
    } catch (e: any) {
      notificationToast("Erreur", e.message, "error");
    } finally {
      setIsResyncing(false);
    }
  }, [product]);

  // Test 4 : classer le produit dans une catégorie choisie à la main (protégée de la synchro).
  const handlePickCategory = useCallback(async (category: Category) => {
    if (!product) return;
    setShowCategoryPicker(false);
    try {
      setSavingCategory(true);
      const updatedProductId = await productsService.setCategory(product.id, category.id);
      setProduct(await productsService.find(updatedProductId));
      notificationToast("Succès", `Catégorie « ${category.name} » affectée.`, "success");
    } catch (e: any) {
      notificationToast("Erreur", e.message, "error");
    } finally {
      setSavingCategory(false);
    }
  }, [product]);

  // Test 4 : revenir au classement automatique (la prochaine synchro reprend la main).
  const handleClearManualCategory = useCallback(async () => {
    if (!product) return;
    try {
      setSavingCategory(true);
      const updatedProductId = await productsService.setCategory(product.id, null);
      setProduct(await productsService.find(updatedProductId));
      notificationToast("Succès", "Classement automatique rétabli.", "success");
    } catch (e: any) {
      notificationToast("Erreur", e.message, "error");
    } finally {
      setSavingCategory(false);
    }
  }, [product]);

  const getStockAgency = useCallback((agencyId: string) => {
    const productStock =  productStocks.find(stock => (stock.agencyId) == agencyId);

    if (productStock && productStock.stock) {
        return productStock.stock.toString();
    }

    return "0";
  }, [productStocks]);

  return (
    <div className="flex flex-col grow h-full">
      <div className="w-full flex flex-col grow">
        {!loading && product && (
          <div className="pb-5xlarge">
            <BackButton
              label={
                !location.search.includes("?from=orders")
                  ? "Retour aux produits"
                  : "Retour à la commande"
              }
              className="mb-xsmall"
            />
            <div className="grid grid-cols-12 gap-x-base">
              <div className="col-span-8 flex flex-col gap-y-xsmall">
                <Section
                  title={product.name}
                  forceDropdown
                  status={
                    <StatusSelector
                      isActive={!product.is_disabled}
                      activeState="Visible"
                      inactiveState="Non visible"
                      onChange={changeVisibility}
                    />
                  }
                >
                  <p className="inter-base-regular text-grey-50 mt-2 whitespace-pre-wrap">
                    {product.description}
                  </p>
                  {product.details.length > 0 &&
                      <div className="flex flex-col gap-y-3 mt-8">
                        {product.details.map((detail, index) => (
                            <Detail key={`detail-${detail.label}-${index}`} title={detail.label} value={detail.value}/>
                        ))}
                      </div>
                  }
                </Section>
                {/*Characteristics are not currently available*/}
                {false &&
                  <Section title={"Caractéristiques"} forceDropdown>
                    <div className="flex flex-col gap-y-3 mt-8">
                      <h2 className="inter-base-semibold">Dimensions</h2>
                      <Detail title="Hauteur" value="" />
                      <Detail title="Largeur" value="-" />
                      <Detail title="Longueur" value="-" />
                      <Detail title="Poids" value="-" />
                    </div>
                  </Section>
                }
                {productDocuments.length > 0 &&
                    <Section title={"Documents associés"} forceDropdown>
                      <div className="flex flex-col gap-y-3 mt-8">
                        {productDocuments.map((productDocument, index) =>
                            <a
                                key={`${productDocument.title}-${index}`}
                                className="flex justify-between items-center inter-base-regular text-grey-50 hover:cursor-pointer"
                                href={productDocument.url}
                                target="_blank"
                            >
                              <p>{productDocument.title}</p>
                                <DownloadIcon />
                            </a>
                        )}
                      </div>
                    </Section>
                }
              </div>
              <div className="flex flex-col col-span-4 gap-y-xsmall">
                <Section title={"Vignette"} forceDropdown>
                  <div className="flex flex-col gap-y-3 mt-8">
                    <div className="aspect-square flex items-center justify-center">
                      <img
                        src={
                          product.image_url
                            ? product.image_url
                            : "/images/placeholder-product.svg"
                        }
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null; // prevents looping
                          currentTarget.src = "/images/placeholder-product.svg";
                        }}
                        alt={`Alt`}
                        className="object-contain rounded-rounded w-full h-full"
                      />
                    </div>
                  </div>
                </Section>
                <Section title={"Données PIM"} forceDropdown>
                  <div className="flex flex-col gap-y-3 mt-8">
                    <Detail title="Source effective" value={product.source ? sourceLabel(product.source) : "Socoda / défaut"} />
                    <Detail title="Fournisseur" value={product.provider} />
                    <Detail title="Marque" value={product.brand?.name} />
                    <Detail
                      title="Règle du fournisseur"
                      value={product.pim_source_rule ? sourceLabel(product.pim_source_rule) : undefined}
                    />
                    <Detail
                      title="Dernière synchro"
                      value={
                        product.pim_sync_at && product.pim_sync_at.getFullYear() > 2000
                          ? DateHelper.dateToDDMMYYYY(product.pim_sync_at)
                          : "Jamais"
                      }
                    />
                    <Button
                      variant="secondary"
                      size="small"
                      loading={isResyncing}
                      onClick={handleResync}
                      className="mt-2 justify-center"
                    >
                      <RefreshIcon size={16} />
                      Resynchroniser ce produit
                    </Button>
                  </div>
                </Section>
                <Section title={"Catégorie"} forceDropdown>
                  <div className="flex flex-col gap-y-3 mt-8">
                    <Detail title="Catégorie" value={product.category?.name} />
                    {product.category_manual && (
                      <p className="inter-small-regular text-grey-40">
                        Catégorie fixée manuellement (la synchronisation ne la modifie plus).
                      </p>
                    )}
                    <Button
                      variant="secondary"
                      size="small"
                      loading={savingCategory}
                      onClick={() => setShowCategoryPicker(true)}
                      className="mt-2 justify-center"
                    >
                      Changer de catégorie
                    </Button>
                    {product.category_manual && (
                      <Button
                        variant="secondary"
                        size="small"
                        loading={savingCategory}
                        onClick={handleClearManualCategory}
                        className="justify-center"
                      >
                        Revenir au classement automatique
                      </Button>
                    )}
                  </div>
                </Section>
                {productAgencies.length > 0 &&
                  <Section title={"Stock"}>
                    <div className="flex flex-col gap-y-3 mt-8">
                            <h2 className="inter-base-semibold">Agences</h2>
                            {productAgencies.map(productAgency =>
                                <Detail
                                    key={`${productAgency.name}-${productAgency.agency_id}`}
                                    title={productAgency.name}
                                    value={getStockAgency(productAgency.agency_id)}
                                />
                            )}
                    </div>
                  </Section>
                }
              </div>
            </div>
          </div>
        )}
        {showCategoryPicker && product && (
          <CategoryPicker
            title="Affecter une catégorie…"
            onClose={() => setShowCategoryPicker(false)}
            onPick={handlePickCategory}
          />
        )}
        {loading && <Spinner size="large" variant="secondary" />}
        {!loading && !product && <NotFound />}
      </div>
    </div>
  );
}

type DetailProps = {
  title: string;
  value?: string | null;
};
const Detail = ({ title, value }: DetailProps) => {
  return (
    <div className="flex justify-between items-center inter-base-regular text-grey-50">
      <p>{title}</p>
      <p>{value ? value : "–"}</p>
    </div>
  );
};
